import React, { useState } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
  TextField,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

export type BrandStringListEditorProps = {
  items: string[];
  onChange: (items: string[]) => void;
  addLabel: string;
  emptyMessage: string;
  listLabel: string;
};

const BrandStringListEditor: React.FC<BrandStringListEditorProps> = ({
  items,
  onChange,
  addLabel,
  emptyMessage,
  listLabel,
}) => {
  const [newValue, setNewValue] = useState("");
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState("");

  const handleAdd = () => {
    const trimmed = newValue.trim();
    if (trimmed && !items.includes(trimmed)) {
      onChange([...items, trimmed]);
      setNewValue("");
    }
  };

  const handleSaveEdit = () => {
    const trimmed = editingValue.trim();
    if (trimmed && editingIndex !== null) {
      const duplicate = items.some(
        (item, i) => i !== editingIndex && item === trimmed,
      );
      if (!duplicate) {
        const next = [...items];
        next[editingIndex] = trimmed;
        onChange(next);
      }
    }
    setEditingIndex(null);
    setEditingValue("");
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Box display="flex" gap={1} mb={1.5}>
        <TextField
          fullWidth
          label={addLabel}
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAdd())}
          size="small"
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!newValue.trim() || items.includes(newValue.trim())}
          sx={{ flexShrink: 0 }}
        >
          Add
        </Button>
      </Box>

      <Typography variant="subtitle2" gutterBottom>
        {listLabel} ({items.length})
      </Typography>
      <List dense disablePadding>
        {items.map((item, index) => (
          <ListItem key={`${item}-${index}`} sx={{ px: 0 }}>
            {editingIndex === index ? (
              <Box display="flex" gap={1} width="100%">
                <TextField
                  fullWidth
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && (e.preventDefault(), handleSaveEdit())
                  }
                  size="small"
                  autoFocus
                />
                <Button
                  size="small"
                  onClick={handleSaveEdit}
                  disabled={
                    !editingValue.trim() ||
                    items.some(
                      (v, i) => i !== index && v === editingValue.trim(),
                    )
                  }
                >
                  Save
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setEditingIndex(null);
                    setEditingValue("");
                  }}
                >
                  Cancel
                </Button>
              </Box>
            ) : (
              <>
                <ListItemText primary={item} />
                <Box>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditingIndex(index);
                      setEditingValue(item);
                    }}
                    sx={{ mr: 0.5 }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => onChange(items.filter((_, i) => i !== index))}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </>
            )}
          </ListItem>
        ))}
        {items.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: "italic", py: 1 }}
          >
            {emptyMessage}
          </Typography>
        )}
      </List>
    </Box>
  );
};

export default BrandStringListEditor;
